import { useState } from 'react';
import { Modal } from 'reactstrap';
import styled from 'styled-components';
import { ImagePlaceMarker } from 'src/modules/community/singlePost/components/imagePlaceMarker';
import i18n from 'src/utils/translate';
import Button from 'src/components/buttons/button';
import { COMMUNITIES_DEFAULT_HIDE_IMAGE } from 'src/utils/imagePreferences';
import { textSmall } from 'styles-js/mixins/typography';
import Icon from 'src/components/icon';
import { ResponsiveImageUnknownSize as Image } from 'src/components/responsiveImage';

type PostImage = {
  imageUrl: string;
  imagePreview?: string;
  imageDescription?: string;
  communityId: number;
  fullscreen?: boolean;
  priority?: boolean;
};

export default function PostImage({ imageUrl, imagePreview, imageDescription = '', communityId, fullscreen = false, priority = false }: PostImage): JSX.Element | null {
  const [isImageHidden, setIsImageHidden] = useState(COMMUNITIES_DEFAULT_HIDE_IMAGE.includes(communityId));
  const [shouldDisplayImageModal, setShouldDisplayImageModal] = useState(false);

  const showPreview = imagePreview && fullscreen;

  if (!imageUrl) return null;

  if (isImageHidden) return <ImagePlaceMarker communityId={communityId} setIsImageHidden={setIsImageHidden} />;

  return (
    <>
      {showPreview ? (
        <PreviewImage>
          <FullscreenButton
            ariaLabel="View the image in full screen mode"
            dataTestId="fullscreen-button"
            type="none"
            onClick={() => setShouldDisplayImageModal(true)}
          >
            <Image
              fill
              alt={imageDescription}
              src={imagePreview}
              unoptimized={true} // TODO: TECH-540
            />
            <FullscreenIcon className="icon icon-zoom-in" />
          </FullscreenButton>
          {imageDescription && <Caption>{imageDescription}</Caption>}
        </PreviewImage>
      ) : (
        <StyledImage
          fill
          alt={imageDescription}
          data-testid="post-image"
          priority={priority}
          src={imageUrl}
          unoptimized={true} // TODO: TECH-540
        />
      )}

      {shouldDisplayImageModal && (
        <ImageModal
          backdropClassName="app-modal__backdrop"
          external={(
            <CloseButton type="tertiary" onClick={() => setShouldDisplayImageModal(false)}>
              {i18n.t('Close')}{' '}<Icon icon="close" />
            </CloseButton>
          )}
          isOpen={shouldDisplayImageModal}
        >
          <Image
            fill
            alt={imageDescription}
            src={imageUrl}
            unoptimized={true} // TODO: TECH-540
          />
        </ImageModal>
      )}
    </>
  );
}

const PreviewImage = styled.div`
  display: inline-block;
  max-width: 350px;
  margin-bottom: 30px;
  border: ${({ theme }) => theme.greyLine};
`;

const FullscreenButton = styled(Button)`
  position: relative;
  margin: 0;
  padding: 0;
  border: none;
  border-radius: 0;
  overflow: hidden;

  &:focus, &:hover, &:active {
    span {
      border-color: ${({ theme }) => theme.colorBlue};
    }
  }

  img {
    max-width: 100%;
  }
`;

const FullscreenIcon = styled.span`
  position: absolute;
  top: 10px;
  right: 10px;
  padding: 5px 8px 7px;
  border-radius: 4px;
  border: ${({ theme }) => theme.greyLine};
  background-color: ${({ theme }) => theme.colorWhite};
  box-shadow: 0 0 6px ${({ theme }) => theme.colorBlackHover};
  font-size: 22px;
  transition: all 0.7s ease;
`;

const Caption = styled.span`
  display: block;
  padding: 10px;
  ${textSmall};
  color: ${({ theme }) => theme.colorGreyDark};
`;

const ImageModal = styled(Modal)`
  .modal &.modal-dialog {
    max-width: none;
    margin: 0;
  }

  .modal-content {
    background-color: transparent;
    background-clip: border-box;
    border: none;

    height: calc(100vh - 75px);
    margin: 60px auto 15px;
    align-items: center;
    justify-content: center;
  }

  img {
    max-width: 100vw;
    max-height: calc(100vh - 75px);
  }
`;

const CloseButton = styled(Button)`
  position: absolute;
  top: 15px;
  right: 15px;
  margin: 0;
  z-index: ${({ theme }) => theme.zindexLiftTop};
`;

const StyledImage = styled(Image)`
  margin-bottom: 30px;
`;
